/**
 * Onboarding/ativação/paywall — contratos do backend:
 *  - POST /app/signup   (app.js:1168; legado showOnboarding.signUp [2859]) — ativa por
 *    email(+código de compra). Respostas: { id, status, startup, timeout, remaining… } |
 *    { warning, time_limit } | { error: reinstall|cheat|invalid_email|invalid_code|
 *    code_already_used }.
 *  - POST /app/purchase (app.js:1864; legado showCTA.signUp [2326]) — autoriza um código
 *    de compra numa conta existente. { expiredAt } | { error }.
 *  - GET  /app/sendcode?email= (app.js:5117) — reenvia o código. { found } | { expired }.
 *  - GET  /app/checkemail?address= (app.js:1810) — polling da confirmação. { active }.
 */
import { api } from '../lib/apiClient';

export interface SignupResponse {
  id?: string;
  status?: string;
  timeout?: string;
  remaining?: string;
  blocked?: boolean;
  startup?: Record<string, unknown>;
  warning?: boolean;
  time_limit?: number;
  error?: string;
  old?: boolean;
  invalid?: boolean;
}

export async function postSignup(data: Record<string, unknown>): Promise<SignupResponse> {
  const result = await api.post<SignupResponse>('app/signup', data, {
    allowEmpty: true,
    rawErrors: true,
    headers: { source: String(data['source'] ?? '') },
  });
  if (!result.ok) throw new Error(result.code);
  return result.data ?? {};
}

export interface PurchaseResponse {
  expiredAt?: string;
  error?: string;
}

export async function postPurchase(email: string, purchase: string, userId: string): Promise<PurchaseResponse> {
  const result = await api.post<PurchaseResponse>('app/purchase', { email, purchase, userId }, { allowEmpty: true, rawErrors: true });
  if (!result.ok) throw new Error(result.code);
  return result.data ?? {};
}

/**
 * Resposta do /app/sendcode. A homologação (handler seguro, 12/10/2026) responde `accepted: true`
 * para qualquer email, para não dizer quem tem código; `rateLimited` traz `retryAfterSeconds`, e
 * `temporarilyUnavailable` vem junto quando o servidor não conseguiu processar. A produção do
 * Classic ainda responde só `found`/`expired`.
 */
export interface SendCodeResponse {
  found?: boolean;
  expired?: boolean;
  accepted?: boolean;
  rateLimited?: boolean;
  retryAfterSeconds?: number;
  temporarilyUnavailable?: boolean;
}

export async function sendCode(email: string): Promise<SendCodeResponse> {
  const result = await api.get<SendCodeResponse>(
    `app/sendcode?email=${encodeURIComponent(email)}`,
    { allowEmpty: true },
  );
  if (!result.ok) throw new Error(result.code);
  return result.data ?? {};
}

export async function checkEmail(address: string): Promise<boolean> {
  const result = await api.get<{ active?: boolean }>(`app/checkemail?address=${encodeURIComponent(address)}`, {
    allowEmpty: true,
  });
  return result.ok && result.data?.active === true;
}
